/**
 * 360° Spin Grid — JavaScript
 *
 * Creates 6 draggable 360-degree spin viewers (one per hair length variant).
 * Desktop: 6-column CSS grid.  Mobile: single-tile carousel with prev/next.
 *
 * Reads `data-base-url` from the section container to construct Shopify Files
 * CDN image URLs.  Each viewer lazy-loads its 24 frames on first interaction.
 */
(function () {
  'use strict';

  var VARIANTS = [
    { id: 'v1', label: '14\u2033', prefix: '360_14_inch_', frames: 24 },
    { id: 'v2', label: '16\u2033', prefix: '360_16_inch_', frames: 24 },
    { id: 'v3', label: '18\u2033', prefix: '360_18_inch_', frames: 24 },
    { id: 'v4', label: '20\u2033', prefix: '360_20_inch_', frames: 24 },
    { id: 'v5', label: '22\u2033', prefix: '360_22_inch_', frames: 24 },
    { id: 'v6', label: '24\u2033', prefix: '360_24_inch_', frames: 24 }
  ];

  /* ── Helpers ────────────────────────────────────────────────────────── */

  function buildFrameUrls(baseUrl, prefix, count) {
    var urls = [];
    for (var i = 1; i <= count; i++) {
      urls.push(baseUrl + prefix + String(i).padStart(3, '0') + '.jpg');
    }
    return urls;
  }

  function preloadFrames(urls) {
    var images = urls.map(function (src) {
      var img = new Image();
      img.src = src;
      return img;
    });
    return Promise.all(
      images.map(function (img) {
        return img.decode().catch(function () {});
      })
    ).then(function () {
      return images;
    });
  }

  /* ── Create a single 360 tile ──────────────────────────────────────── */

  function createTile(v, baseUrl, instances) {
    var tile = document.createElement('div');
    tile.className = 'tile';

    tile.innerHTML =
      '<div class="viewer" data-variant="' + v.id + '" tabindex="0">' +
        '<div class="tile-header">' + v.label + '</div>' +
        '<img class="poster" src="' + baseUrl + v.prefix + '001.jpg" alt="' + v.label + ' hair extension 360 view" loading="lazy" />' +
        '<div class="status drag-pill" data-role="status">' +
          '<span>DRAG TO SPIN</span>' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M21 12H3m14-4l4 4-4 4M7 8l-4 4 4 4" />' +
          '</svg>' +
        '</div>' +
      '</div>';

    var viewer = tile.querySelector('.viewer');
    var statusEl = tile.querySelector('[data-role="status"]');

    /* Lazy initialisation — runs once on first hover or touch */
    var init = function () {
      if (instances.has(v.id)) return;
      statusEl.querySelector('span').textContent = 'LOADING\u2026';

      var urls = buildFrameUrls(baseUrl, v.prefix, v.frames);

      preloadFrames(urls).then(function (imgElements) {
        if (instances.has(v.id)) return;

        var currentIndex = 0;
        var count = imgElements.length;

        imgElements.forEach(function (img, i) {
          img.className = 'frame';
          img.draggable = false;
          if (i !== 0) img.style.visibility = 'hidden';
          viewer.appendChild(img);
        });

        var inst = {
          goto: function (i) {
            imgElements[currentIndex].style.visibility = 'hidden';
            currentIndex = ((i % count) + count) % count;
            imgElements[currentIndex].style.visibility = 'visible';
          },
          next: function () { this.goto(currentIndex + 1); },
          prev: function () { this.goto(currentIndex - 1); }
        };

        instances.set(v.id, inst);

        /* Hide poster, restore pill text */
        var poster = viewer.querySelector('.poster');
        if (poster) poster.style.display = 'none';
        statusEl.querySelector('span').textContent = 'DRAG TO SPIN';

        /* ── Keyboard navigation ─────────────────────────────────────── */
        viewer.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowLeft')  { e.preventDefault(); inst.prev(); }
          if (e.key === 'ArrowRight') { e.preventDefault(); inst.next(); }
        });

        /* ── Pointer drag with inertia ───────────────────────────────── */
        var DRAG_THRESHOLD = 6;
        var FRICTION = 0.92;
        var MIN_VELOCITY = 0.4;
        var STEP_PX = 8;

        var dragging = false;
        var lastDragX = 0;
        var velocitySamples = [];
        var inertiaRaf = null;

        function cancelInertia() {
          if (inertiaRaf) { cancelAnimationFrame(inertiaRaf); inertiaRaf = null; }
        }

        function startInertia() {
          if (velocitySamples.length < 2) return;
          var first = velocitySamples[0];
          var last  = velocitySamples[velocitySamples.length - 1];
          var dt = last.t - first.t;
          if (dt < 1) return;
          var vel = (last.x - first.x) / dt;
          velocitySamples = [];
          if (Math.abs(vel) < MIN_VELOCITY) return;

          var accum = 0;
          var tick = function () {
            vel *= FRICTION;
            if (Math.abs(vel) < MIN_VELOCITY) return;
            accum += vel * 16;
            while (Math.abs(accum) >= STEP_PX) {
              if (accum > 0) { inst.next(); accum -= STEP_PX; }
              else           { inst.prev(); accum += STEP_PX; }
            }
            inertiaRaf = requestAnimationFrame(tick);
          };
          inertiaRaf = requestAnimationFrame(tick);
        }

        viewer.addEventListener('pointerdown', function (e) {
          e.preventDefault();
          viewer.classList.add('started');
          cancelInertia();
          dragging = true;
          lastDragX = e.clientX;
          velocitySamples = [{ x: e.clientX, t: performance.now() }];
          viewer.setPointerCapture(e.pointerId);
        });

        viewer.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          e.preventDefault();
          var dx = e.clientX - lastDragX;
          if (Math.abs(dx) >= DRAG_THRESHOLD) {
            if (dx > 0) inst.next(); else inst.prev();
            lastDragX = e.clientX;
          }
          var now = performance.now();
          velocitySamples.push({ x: e.clientX, t: now });
          while (velocitySamples.length > 2 && now - velocitySamples[0].t > 80) {
            velocitySamples.shift();
          }
        });

        function endDrag() {
          if (!dragging) return;
          dragging = false;
          startInertia();
        }

        viewer.addEventListener('pointerup', endDrag);
        viewer.addEventListener('pointercancel', endDrag);
      });
    };

    /* Trigger init on first hover or touch */
    viewer.addEventListener('pointerenter', init, { once: true });
    viewer.addEventListener('pointerdown',  init, { once: true });

    return tile;
  }

  /* ── Mount tiles + mobile carousel ─────────────────────────────────── */

  function initSpinGrid(section) {
    var baseUrl = section.dataset.baseUrl;
    if (!baseUrl) return;

    var instances = new Map();
    var root = section.querySelector('.spin-grid__grid');
    if (!root) return;

    /* Create and append all 6 tiles */
    VARIANTS.forEach(function (v) {
      root.appendChild(createTile(v, baseUrl, instances));
    });

    /* ── Mobile carousel ─────────────────────────────────────────────── */
    var prevBtn   = section.querySelector('.spin-grid__nav-btn--prev');
    var nextBtn   = section.querySelector('.spin-grid__nav-btn--next');
    var prevLabel = section.querySelector('[data-role="prev-label"]');
    var nextLabel = section.querySelector('[data-role="next-label"]');
    var dotsRoot  = section.querySelector('[data-role="dots"]');

    if (!prevBtn || !nextBtn || !dotsRoot) return;

    var tiles   = root.querySelectorAll('.tile');
    var total   = tiles.length;
    var current = 0;

    /* Build dot indicators */
    VARIANTS.forEach(function (v, i) {
      var dot = document.createElement('button');
      dot.className = 'spin-grid__dot' + (i === 0 ? ' active' : '');
      dot.setAttribute('aria-label', 'Go to ' + v.label);
      dot.addEventListener('click', function () { goTo(i); });
      dotsRoot.appendChild(dot);
    });

    function goTo(index) {
      current = ((index % total) + total) % total;

      tiles.forEach(function (tile) {
        tile.style.transform = 'translateX(-' + (current * 100) + '%)';
      });

      var prevIdx = (current - 1 + total) % total;
      var nextIdx = (current + 1) % total;
      if (prevLabel) prevLabel.textContent = VARIANTS[prevIdx].label;
      if (nextLabel) nextLabel.textContent = VARIANTS[nextIdx].label;

      var dots = dotsRoot.querySelectorAll('.spin-grid__dot');
      dots.forEach(function (d, i) {
        d.classList.toggle('active', i === current);
      });
    }

    prevBtn.addEventListener('click', function () { goTo(current - 1); });
    nextBtn.addEventListener('click', function () { goTo(current + 1); });
    goTo(0);
  }

  /* ── Bootstrap ─────────────────────────────────────────────────────── */

  function boot() {
    document.querySelectorAll('.spin-grid-section').forEach(initSpinGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
    